import { GitBranch, Link as LinkIcon, Paperclip, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { fmtDate } from '../../../shared/format'
import { useTaskActions } from '../../taskApi'
import type { Project, Task } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { FileList, UploadButton } from '../FileList'
import { TaskStatusBadge } from '../StatusBadges'
import { TaskFormModal } from './TaskFormModal'

/** Alt görevler, bağımlılıklar ve ekler. */
export function TaskRelations({ project, task, canWrite }: { project: Project; task: Task; canWrite: boolean }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const { addDependency, removeDependency } = useTaskActions(project.id, task.id)
  const [subOpen, setSubOpen] = useState(false)
  const [dep, setDep] = useState('')
  const subtasks = project.tasks.filter((x) => x.parentId === task.id)
  const deps = task.dependsOn.map((id) => project.tasks.find((x) => x.id === id)).filter((x): x is Task => !!x)
  const files = project.files.filter((f) => f.taskId === task.id)
  const depOptions = project.tasks.filter((x) => x.id !== task.id && !task.dependsOn.includes(x.id)).map((x) => ({ value: x.id, label: `${x.key} · ${x.title}`, keywords: [x.key, x.title] }))

  const add = () => {
    if (!dep) return
    addDependency.mutate(dep, {
      onSuccess: () => setDep(''),
      onError: toastError,
    })
  }

  const head = (icon: typeof GitBranch, title: string, action?: React.ReactNode) => {
    const Icon = icon
    return (
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="flex-1 font-semibold">{title}</h2>
        {action}
      </header>
    )
  }

  return (
    <>
      <section className="@container rounded-xl border bg-card shadow-card">
        {head(
          GitBranch,
          t('projects.taskDetail.subtasks', { count: subtasks.length }),
          canWrite ? (
            <Button size="sm" variant="outline" onClick={() => setSubOpen(true)}>
              <Plus />
              {t('projects.taskDetail.addSubtask')}
            </Button>
          ) : null,
        )}
        {subtasks.length === 0 ? (
          <p className="px-4 py-5 text-sm text-muted-foreground">{t('projects.taskDetail.noSubtasks')}</p>
        ) : (
          <ul className="divide-y">
            {subtasks.map((s) => (
              <li key={s.id} className="flex flex-col gap-2 px-4 py-2.5 @2xl:flex-row @2xl:items-center @2xl:gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Link to={`/projects/${project.id}/tasks/${s.id}`} className="w-20 shrink-0 font-mono text-xs text-primary hover:underline">
                    {s.key}
                  </Link>
                  <span className="min-w-0 flex-1 truncate text-sm">{s.title}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 text-xs">
                    {s.assigneeId ? <Avatar name={nameOf(s.assigneeId)} className="size-6 [&>span]:text-[9px]" /> : null}
                    <span className="truncate">{s.assigneeId ? nameOf(s.assigneeId) : '—'}</span>
                  </span>
                  <TaskStatusBadge status={s.status} />
                  <span className="ml-auto text-xs tabular-nums text-muted-foreground @2xl:ml-0 @2xl:w-24 @2xl:text-right">{fmtDate(s.dueDate)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border bg-card shadow-card">
        {head(LinkIcon, t('projects.taskDetail.dependencies', { count: deps.length }))}
        <div className="space-y-2 p-4">
          {deps.length === 0 ? <p className="text-sm text-muted-foreground">{t('projects.taskDetail.noDependencies')}</p> : null}
          {deps.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to={`/projects/${project.id}/tasks/${d.id}`} className="w-20 shrink-0 font-mono text-xs text-primary hover:underline">
                {d.key}
              </Link>
              <span className="min-w-0 flex-1 basis-40 truncate text-sm">{d.title}</span>
              <TaskStatusBadge status={d.status} />
              {canWrite ? (
                <Button size="icon" variant="ghost" className="size-7" aria-label={t('projects.taskDetail.removeDependency', { key: d.key })} onClick={() => removeDependency.mutate(d.id, { onError: toastError })}>
                  <X />
                </Button>
              ) : null}
            </div>
          ))}
          {canWrite && depOptions.length ? (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <Select className="min-w-0 flex-1" size="sm" value={dep} onChange={setDep} options={depOptions} searchable placeholder={t('projects.taskDetail.pickDependency')} aria-label={t('projects.taskDetail.pickDependency')} />
              <Button size="sm" variant="outline" onClick={add} disabled={!dep || addDependency.isPending}>
                <Plus />
                {t('common.add')}
              </Button>
            </div>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border bg-card shadow-card">
        {head(Paperclip, t('projects.taskDetail.attachmentsCount', { count: files.length }), canWrite ? <UploadButton project={project} taskId={task.id} /> : null)}
        <div className="p-4">
          <FileList project={project} files={files} canWrite={canWrite} layout="grid" />
        </div>
      </section>

      <TaskFormModal open={subOpen} project={project} task={null} defaults={{ parentId: task.id, type: task.type, installationId: task.installationId }} onOpenChange={setSubOpen} />
    </>
  )
}
